/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IApiSessionSearchCardReq,
  IApiSessionSearchCardResp,
  IApiSessionSearchCardOtherReq,
  IApiSessionSearchCardOtherResp,
  IApiSubMessageDto,
} from '@/services/swagger/easourcing/models';
import { companySummaryPost, searchCardOtherPost, searchCardPost } from '@/services/swagger/easourcing/SessionService';
import { createContext, useContext } from 'react';
import { create, UseBoundStore, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { SearchCardSubMsg } from '../types';
import { take } from 'lodash-es';
export type SupplierListParams = Pick<
  Required<IApiSessionSearchCardOtherReq>,
  'sessionId' | 'messageId' | 'subMessageId'
> & {
  subMsgData: IApiSubMessageDto;
};
/**
 * supplierList 状态管理 store 接口定义
 */
interface SupplierListStoreState {
  /**true 展示供应商大列表页的骨架屏，false 不展示 */
  showListDetailSkeleton: boolean;
  /**true 展示供应商大列表页的弹窗，false 不展示 */
  showListDetailModal: boolean;

  // 原始数据
  supplierListData: IApiSessionSearchCardResp | null;
  supplierOtherData: IApiSessionSearchCardOtherResp | null;
  supplierSummaryData: any | null;

  // 聚合数据
  aggregatedSuppliers: any[];

  /**在回话列表里，展示的前5个供应商，只要筛选条件改变，就会更新，如果是页面切换，就不会更新，直到筛选条件改变，才会更新。 */
  topFiveSuppliers: any[];

  // 加载状态
  isLoading: boolean;
  isOtherDataLoading: boolean;
  isSummaryLoading: boolean;
  isTopFviewOtherDataLoading: boolean;
  isTopFviewSummaryLoading: boolean;

  // 错误状态
  error: string | null;

  // 搜索参数
  lastParams: Partial<IApiSessionSearchCardReq>;

  // 方法
  getSupplierList: (
    params: Partial<IApiSessionSearchCardReq>,
    extra: {
      isFilterChange: boolean;
    }
  ) => Promise<void>;
  aggregateData: (updateTopFive: boolean) => void;
  clearData: () => void;
  setStoreState: (state: Partial<SupplierListStoreState>) => void;
}

/**
 * 聚合供应商数据的纯函数
 * @param supplierListData - 供应商列表数据
 * @param supplierOtherData - 供应商其他数据
 * @param supplierSummaryData - 供应商摘要数据
 * @returns 聚合后的供应商数据数组
 */
const aggregateSupplierData = (
  supplierListData: IApiSessionSearchCardResp | null,
  supplierOtherData: IApiSessionSearchCardOtherResp | null,
  supplierSummaryData: any | null
): any[] => {
  if (!supplierListData || !supplierListData.companyList) {
    return [];
  }

  return supplierListData.companyList.map(item => {
    const other = supplierOtherData?.companyList?.find((item2: any) => item2.id === item.id) || {};
    const summary =
      supplierSummaryData?.companySummaryDtos?.find((item2: any) => item2.companyId === item.companyId) || {};
    return {
      ...item,
      ...other,
      ...summary,
    };
  });
};

/**
 * 创建 supplierList 状态管理 store
 * @returns
 */
export const createSupplierListStore = (params: SupplierListParams) => {
  console.log('params', params);
  return create<SupplierListStoreState>()(
    immer((set, _get) => {
      return {
        showListDetailSkeleton: true,
        showListDetailModal: false,
        // 初始状态
        supplierListData: null,
        supplierOtherData: null,
        supplierSummaryData: null,
        aggregatedSuppliers: [],
        topFiveSuppliers: [],
        isLoading: false,
        isOtherDataLoading: false,
        isSummaryLoading: false,
        isTopFviewOtherDataLoading: false,
        isTopFviewSummaryLoading: false,
        error: null,
        lastParams: { page: 1, pageSize: 5 },

        // 聚合数据方法
        aggregateData: (updateTopFive = false) => {
          const { supplierListData, supplierOtherData, supplierSummaryData } = _get();
          const aggregatedSuppliers = aggregateSupplierData(supplierListData, supplierOtherData, supplierSummaryData);
          set({ aggregatedSuppliers });
          if (updateTopFive) {
            set({ topFiveSuppliers: take(aggregatedSuppliers, 5) });
          }
        },

        // 方法
        getSupplierList: async (requestParams: Partial<IApiSessionSearchCardReq>, extra) => {
          const isFilterChange = extra.isFilterChange;
          try {
            set({ isLoading: true, error: null });

            // 处理请求参数
            const newParams = {
              ..._get().lastParams,
              ...requestParams,
            };
            set({ lastParams: newParams });

            // 发起供应商列表接口请求
            const res = await searchCardPost(newParams);
            const supplierListData = res.data;
            set({ supplierListData, showListDetailSkeleton: false });

            // 聚合数据（供应商列表返回后立即聚合）
            _get().aggregateData(isFilterChange);

            // 如果有供应商列表数据，发起其他两个接口的请求
            if (supplierListData && supplierListData.companyList && supplierListData.companyList.length > 0) {
              const { sessionId, messageId, subMessageId } = params;
              const { queryProduct, otherLanguage } = (params.subMsgData as unknown as SearchCardSubMsg).contentValue;

              if (isFilterChange) {
                set({
                  isTopFviewOtherDataLoading: true,
                  isTopFviewSummaryLoading: true,
                });
              }
              set({ isOtherDataLoading: true, isSummaryLoading: true });

              const getOhters = async () => {
                // 发起供应商其他数据接口请求
                const otherRes = await searchCardOtherPost({
                  sessionId,
                  messageId,
                  subMessageId,
                  globalSearchIdList: supplierListData.companyList.map(item => item.id),
                  queryValue: queryProduct,
                  otherGoodsShipped: otherLanguage.map(item => item.product),
                });
                set({ supplierOtherData: otherRes.data, isOtherDataLoading: false, isTopFviewOtherDataLoading: false });

                // 聚合数据（其他数据返回后立即聚合）
                _get().aggregateData(isFilterChange);
              };

              const getSummary = async () => {
                // 发起供应商摘要接口请求
                const summaryRes = await companySummaryPost({
                  sessionId,
                  messageId,
                  subMessageId,
                  queryValue: queryProduct,
                  companyInfo: supplierListData.companyList,
                  otherGoodsShipped: otherLanguage.map(item => item.product),
                  targetCountries: newParams.country,
                });
                set({ supplierSummaryData: summaryRes.data, isSummaryLoading: false, isTopFviewSummaryLoading: false });

                // 聚合数据（摘要数据返回后立即聚合）
                _get().aggregateData(isFilterChange);
              };
              // 并行发起其他数据和摘要接口请求
              Promise.allSettled([getOhters(), getSummary()]);
            }
          } catch (error) {
            set({ error: error instanceof Error ? error.message : 'An error occurred', isLoading: false });
          } finally {
            set({ isLoading: false });
          }
        },
        clearData: () => {
          set({
            supplierListData: null,
            supplierOtherData: null,
            supplierSummaryData: null,
            aggregatedSuppliers: [],
            error: null,
          });
        },
        setStoreState: (state: Partial<SupplierListStoreState>) => set(state),
      };
    })
  );
};
/**
 * supplierList 状态管理 store 类型定义
 */
type SupplierListStore = ReturnType<typeof createSupplierListStore>;

/**
 * supplierList 状态管理 store 上下文定义
 *
 */
export const SupplierListStoreContext = createContext<SupplierListStore | null>(null);

/**
 * supplierList 状态管理 store 选择器定义
 * @param selector
 * @returns
 */
export const useSupplierListStore = (selector => {
  const store = useContext(SupplierListStoreContext);
  if (!store) throw new Error('Missing SupplierListStoreContext.Provider in the tree');
  return useStore(store, selector);
}) as UseBoundStore<SupplierListStore>;
